import React, { useState } from 'react';
import { useAgencyCapabilities } from '../shared/agency/AgencyEntry';
import { 
  User, 
  Package,
  Truck,
  LogOut, 
  ChevronRight,
  MessageCircle,
  Radio,
  BarChart3,
  MapPin,
  Zap,
  X,
  ScanBarcode, 
  Sparkles,
  DollarSign,
  Clock,
  Target,
  Rocket 
} from 'lucide-react';

function OptionsDriver({
  username,
  setActiveComponent, 
  onLogout,
  chatUnreadCount = 0,
}) {
  const [showEntregasLibresModal, setShowEntregasLibresModal] = useState(false);
  const agency = useAgencyCapabilities();

  const menuItems = [
    ...(agency?.enabled ? [{ id: 'AgencyWorkspace', label: 'Despachos a agencias', icon: Package,
      onClick: () => setActiveComponent('AgencyWorkspace'), color: 'from-[#B1336E] to-slate-700' }] : []),
    {
      id: 'MisRecojos',
      label: 'Mis Recojos',
      icon: Package,
      onClick: () => setActiveComponent('MisRecojos'),
      color: 'from-[#B1336E] to-[#E6B43C]',
    },
    {
      id: 'MisEntregas',
      label: 'Mis Entregas',
      icon: Truck,
      onClick: () => setActiveComponent('MisEntregas'),
      color: 'from-[#67A43C] to-[#E6B43C]',
    },
    {
      id: 'Expresss',
      label: 'Mis Entregas Express',
      icon: Rocket,
      onClick: () => setActiveComponent('DriverExpressDashboard'),
      color: 'from-[#67A43C] to-[#E6B43C]',
    },
    {
      id: 'Chat',
      label: 'Chat',
      icon: MessageCircle,
      onClick: () => setActiveComponent('Chat'),
      color: 'from-[#B1336E] to-[#67A43C]',
      badge: chatUnreadCount > 0 ? String(chatUnreadCount) : null,
    },
    {
      id: 'Escanear',
      label: 'Escanear',
      icon: ScanBarcode,
      onClick: () => setActiveComponent('DriverPackageScanner'),
      color: 'from-[#B1336E] to-[#67A43C]',
    },
    {
      id: 'ControlTower',
      label: 'Torre de Control',
      icon: Radio,
      onClick: () => setActiveComponent('ControlTower'),
      color: 'from-[#67A43C] to-[#E6B43C]',
    },
    {
      id: 'Resumen',
      label: 'Resumen',
      icon: BarChart3,
      onClick: () => setActiveComponent('Resumen'),
      color: 'from-[#E6B43C] to-[#B1336E]',
    },
    {
      id: 'UbicacionFinal',
      label: 'Ubicación Final',
      icon: MapPin,
      onClick: () => setActiveComponent('MapSelectionDriver'),
      color: 'from-[#67A43C] to-[#B1336E]',
    },
    {
      id: 'EntregasLibres',
      label: 'Entregas Libres',
      icon: Zap,
      onClick: () => setActiveComponent('ChooseDeliveries'),
      color: 'from-[#B1336E] to-[#E6B43C]',
      badge: 'Nuevo',
    },
  ];

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Fixed Header with Fast Max Branding */}
      <div className="flex-shrink-0 bg-gradient-to-r from-brand-primary to-pink-600 shadow-md">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo/Brand Section */}
            <div className="flex items-center gap-3">
              <div className="bg-white p-2 rounded-lg shadow-sm">
                <Package className="w-6 h-6 text-brand-primary" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-extrabold text-white font-montserrat tracking-tight">
                  FAST MAX
                </h1>
                <p className="text-xs text-white/90 font-montserrat">
                  Conductor
                </p>
              </div>
            </div>

            {/* User Info Badge */}
            <div className="bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full hidden sm:block">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-white" />
                <span className="text-sm font-medium text-white font-montserrat">
                  {username || 'Conductor'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8 sm:py-8">
          
          {/* Profile Card */}
          <button
            onClick={() => setActiveComponent('MyAccountDriver')}
            className="w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:shadow-md hover:border-[#B1336E]/30 transition-all duration-200 mb-6"
          >
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-[#B1336E] to-[#E6B43C] flex items-center justify-center shadow-lg flex-shrink-0">
                <User className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
              </div>
              <div className="flex-1 text-left min-w-0">
                <h2 className="text-lg sm:text-xl font-extrabold text-[#B1336E] font-montserrat truncate">
                  {username || 'Usuario'}
                </h2>
                <p className="text-sm text-slate-600 font-montserrat">Mi cuenta</p>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
            </div>
          </button>

          {/* Menu Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {menuItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={item.onClick}
                  className="group relative bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
                >
                  {item.badge && (
                    <div className="absolute -top-2 -right-2 bg-gradient-to-r from-[#E6B43C] to-[#B1336E] text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg font-montserrat">
                      {item.badge}
                    </div>
                  )}
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow duration-200`}>
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="text-base sm:text-lg font-semibold text-slate-900 font-montserrat group-hover:text-[#B1336E] transition-colors duration-200">
                        {item.label}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-[#B1336E] group-hover:translate-x-1 transition-all duration-200" />
                  </div>
                </button>
              );
            })}
          </div>

        </div>
      </div>

      {/* Fixed Footer with Logout */}
      <div className="flex-shrink-0 bg-white border-t border-slate-200 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <button
            onClick={onLogout}
            className="w-full bg-white hover:bg-red-50 border-2 border-red-200 hover:border-red-300 text-red-600 font-semibold py-3 px-4 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-sm font-montserrat"
          >
            <LogOut className="w-5 h-5" />
            <span>Cerrar Sesión</span>
          </button>
        </div>
      </div>

      {/* Entregas Libres Coming Soon Modal */}
      {showEntregasLibresModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 backdrop-blur-sm">
          <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="relative p-4">
              {/* Close Button */}
              <button
                onClick={() => setShowEntregasLibresModal(false)}
                className="absolute top-3 right-3 p-1.5 hover:bg-slate-100 rounded-lg transition-colors z-10"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>

              {/* Header with Icon */}
              <div className="flex flex-col items-center mb-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#B1336E] to-[#E6B43C] mb-3 shadow-lg">
                  <Zap className="h-7 w-7 text-white" />
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-[#E6B43C] animate-pulse" />
                  <h3 className="text-xl font-extrabold text-[#B1336E] font-montserrat">
                    ¡Próximamente!
                  </h3>
                  <Sparkles className="w-4 h-4 text-[#E6B43C] animate-pulse" />
                </div>
              </div>

              {/* Content */}
              <div className="space-y-3 mb-4">
                <p className="text-sm text-slate-700 font-montserrat leading-relaxed text-center">
                  Prepárate para una <span className="font-bold text-[#B1336E]">revolución en tu forma de trabajar</span>. Estamos desarrollando algo extraordinario para ti.
                </p>

                <div className="bg-gradient-to-r from-[#B1336E]/10 to-[#E6B43C]/10 rounded-xl p-3 border-l-4 border-[#B1336E]">
                  <h4 className="font-bold text-slate-900 font-montserrat text-sm mb-1 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-[#B1336E]" />
                    Entregas Libres
                  </h4>
                  <p className="text-xs text-slate-700 font-montserrat leading-relaxed">
                    <span className="font-semibold text-[#B1336E]">Tú decides cuándo, dónde y cuánto trabajar.</span> La libertad total de elegir tus entregas está llegando a Fast Max.
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-start gap-2 bg-white rounded-lg p-2.5 border border-slate-200">
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-[#67A43C] to-[#E6B43C] flex items-center justify-center">
                      <Target className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-bold text-slate-900 font-montserrat text-xs mb-0.5">
                        Elige tus zonas
                      </h5>
                      <p className="text-xs text-slate-600 font-montserrat leading-snug">
                        Selecciona las áreas donde prefieres trabajar y ve las entregas disponibles en tiempo real
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 bg-white rounded-lg p-2.5 border border-slate-200">
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-[#B1336E] to-[#E6B43C] flex items-center justify-center">
                      <Clock className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-bold text-slate-900 font-montserrat text-xs mb-0.5">
                        Tu horario, tus reglas
                      </h5>
                      <p className="text-xs text-slate-600 font-montserrat leading-snug">
                        Trabaja cuando quieras. Acepta entregas en el momento que mejor te convenga
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 bg-white rounded-lg p-2.5 border border-slate-200">
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-[#E6B43C] to-[#B1336E] flex items-center justify-center">
                      <DollarSign className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-bold text-slate-900 font-montserrat text-xs mb-0.5">
                        Gana por cada entrega
                      </h5>
                      <p className="text-xs text-slate-600 font-montserrat leading-snug">
                        Comisión directa por cada paquete entregado. Mientras más entregas, más ganas
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-[#67A43C]/20 via-[#E6B43C]/20 to-[#B1336E]/20 rounded-xl p-3 border-2 border-dashed border-[#B1336E]/30">
                  <p className="text-xs text-slate-800 font-montserrat text-center leading-relaxed font-semibold">
                    🚀 <span className="text-[#B1336E]">Maximiza tus ingresos</span> y <span className="text-[#67A43C]">trabaja con total flexibilidad</span>. El futuro de las entregas está en tus manos.
                  </p>
                </div>

                <div className="bg-[#B1336E]/5 rounded-lg p-2.5 text-center">
                  <p className="text-xs text-slate-600 font-montserrat italic">
                    Te notificaremos apenas esta función esté disponible. ¡Prepárate para una nueva era de entregas! 🎯
                  </p>
                </div>
              </div>

              {/* Footer */}
              <button
                onClick={() => setShowEntregasLibresModal(false)}
                className="w-full rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-md hover:shadow-lg transition-all bg-gradient-to-r from-[#B1336E] to-[#E6B43C] font-montserrat"
              >
                ¡Genial! Avísenme cuando esté lista 🔥
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OptionsDriver;
